import type { MaybeRefOrGetter } from 'vue'

/**
 * Styles that keep a full-screen `position: fixed; top: 0` panel visible while the
 * on-screen keyboard is open, while `active` is true.
 *
 * `100dvh` ignores the keyboard, so the panel is sized to the visual viewport instead.
 * Safari additionally reveals a focused field by panning the page, which drags the top
 * of the panel off screen; since the shrunken panel already keeps the field visible,
 * the pan is undone with `scrollTo` whenever it appears. Following the pan via
 * `visualViewport.offsetTop` is not an option: iOS 26 shrinks `innerHeight` for the
 * keyboard yet still pans, and leaves stale offsets after dismissal. The panel must
 * also stay transform-free — iOS mispositions transformed fixed elements once the
 * keyboard is open.
 */
export default (active: MaybeRefOrGetter<boolean>) => {
  const viewport = import.meta.client ? window.visualViewport : null

  const height = ref(0)

  function measure() {
    if (!viewport || !toValue(active))
      return

    height.value = Math.min(viewport.height, window.innerHeight)
    if (window.scrollY || viewport.offsetTop)
      window.scrollTo(0, 0)
  }

  watch(() => toValue(active), value => value && measure(), { immediate: true })
  useEventListener(viewport, ['resize', 'scroll'], measure, { passive: true })

  return computed(() => height.value === 0
    ? undefined
    : { height: `${height.value}px` })
}
